'use client';
import React, { useState, useCallback, useEffect, useRef, ReactElement } from 'react';
import { format } from 'date-fns';
import styled from '@benzinga/themetron';
import Hooks from '@benzinga/hooks';
import { isMobile } from '@benzinga/device-utils';
import { formatTickerToURLFriendly } from '@benzinga/utils';
import { Ticker } from '@benzinga/session';
import { SearchBar } from './SearchBar';
import { TrackingManager } from '@benzinga/tracking-manager';
import { useRouter } from 'next/navigation';
import { SessionContext } from '@benzinga/session-context';
import { AutocompleteManager } from '@benzinga/autocomplete-manager';
import { QuotesManager, injectLogoInTickers } from '@benzinga/quotes-manager';
import { BasicNewsManager } from '@benzinga/basic-news-manager';

export interface Article {
  link: string;
  pub_date: string;
  title: string;
}

export type SearchType = 'all' | 'ticker' | 'article' | 'keyword';
export type SearchVariant = 'default' | 'box' | 'navbar' | 'inline';
const MAX_SEARCH_RESULTS = 5;

export interface SearchProps {
  autoFocus?: boolean;
  handleRemove?: (removed: Ticker) => void;
  onSearching?: (boolean: boolean) => void;
  onSelect?: (selected: Ticker) => void;
  onToggle?: (value?: boolean) => void;
  placeholder?: string;
  query?: string;
  searchType?: SearchType;
  selected?: Ticker[] | Ticker | null;
  variant?: SearchVariant;
  isFullScreenSearch?: boolean;
}

const selectElementListClass = 'search-list-item';

const SEARCH_ITEM_LOGO_SIZE = 40;
const IMAGE_REQUEST_PARAMS = {
  fields: 'logo_vector_light',
  scale: `${SEARCH_ITEM_LOGO_SIZE}x${SEARCH_ITEM_LOGO_SIZE}`,
  search_keys_type: 'symbol',
};

export const Search: React.FC<SearchProps> = ({
  autoFocus,
  handleRemove,
  isFullScreenSearch = false,
  onSearching,
  onSelect,
  onToggle,
  placeholder = 'Tickers, Articles or Keywords',
  query,
  searchType = 'all',
  selected,
  variant = 'default',
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [foundedTickers, setFoundedTickers] = useState<Ticker[]>([]);
  const [foundedArticles, setFoundedArticles] = useState<Article[]>([]);
  const [fetchDebounce, setFetchDebounce] = useState<number>();
  const [isFetching, setIsFetching] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState<number>(-1);
  const [showDropdown, setShowDropdown] = useState<boolean>(true);
  const [searchResultsHeight, setSearchResultsHeight] = useState<string>('auto');
  const [pendingKeyDownTickerSearch, setPendingKeyDownTickerSearch] = useState<string | null>(null);

  const searchBarRef = useRef<HTMLInputElement | null>(null);

  const articlesSearch = searchType === 'all' || searchType === 'article';
  const keywordsSearch = searchType === 'all' || searchType === 'keyword';
  const tickersSearch = searchType === 'all' || searchType === 'ticker';

  const router = useRouter();
  const ref = useRef<HTMLDivElement | null>(null);

  const session = React.useContext(SessionContext);
  const autocompleteManager = session.getManager(AutocompleteManager);
  const quotesManager = session.getManager(QuotesManager);

  Hooks.useEventListener('resize', () => {
    if (isMobile()) {
      const windowHeightSeventyPercent = `${Math.round(window.innerHeight * 0.7)}px`;
      setSearchResultsHeight(windowHeightSeventyPercent);
    }
  });

  Hooks.useClickOutside(ref, () => {
    setSelectedIndex(-1);
    setShowDropdown(false);
  });

  React.useEffect(() => {
    if (
      foundedTickers?.length &&
      foundedTickers[0]?.symbol?.toLowerCase() === pendingKeyDownTickerSearch?.toLowerCase()
    ) {
      router.push(`/quote/${formatTickerToURLFriendly(foundedTickers[0]?.symbol)}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingKeyDownTickerSearch, foundedTickers]);

  const updateTickersLogos = useCallback(
    async (incomeTickers: Ticker[], symbolsList?: string[]) => {
      try {
        const tickers = incomeTickers ? [...incomeTickers] : [];

        const { ok: logos } = await quotesManager.getQuotesLogosMapped(
          symbolsList ?? tickers.map(item => item.symbol),
          IMAGE_REQUEST_PARAMS,
        );

        if (logos) {
          setFoundedTickers(injectLogoInTickers(tickers, logos));
        }
      } catch (e) {
        console.error('LOGOS ERROR', e);
      }
    },
    [quotesManager],
  );

  const fetchTickers = useCallback(
    async (query: string) => {
      try {
        const res = await autocompleteManager.getAutocompleteListing(query);
        let tickers: Ticker[] = res?.ok?.result.slice(0, 5) ?? [];
        const tickersList = tickers.map(item => item.symbol);
        const tickersLogos = quotesManager.getQuotesLogosMappedCached(tickersList, IMAGE_REQUEST_PARAMS);
        tickers = injectLogoInTickers(tickers, tickersLogos);

        updateTickersLogos(tickers, tickersList);
        setFoundedTickers(tickers);

        return tickers;
      } catch (error) {
        console.error('TICKERS ERROR:', error);
        return [];
      }
    },
    [autocompleteManager, updateTickersLogos, quotesManager],
  );

  const fetchArticles = useCallback(
    async (query: string) => {
      const articles: Article[] = [];
      try {
        const basicNewsManager = session.getManager(BasicNewsManager);

        basicNewsManager
          .simplyQueryNews({ search: query }, { limit: MAX_SEARCH_RESULTS, sortOrderCreated: 'desc' })
          .then(res => {
            const data = res?.ok;

            const articles =
              data?.map(article => {
                const url: string | undefined = article?.url as string | undefined;
                const nodeId: number | undefined = article?.nodeId as number | undefined;
                const isRootUrl = url === 'https://www.benzinga.com/' || url === 'https://www.benzinga.com';
                const articleLink =
                  (isRootUrl && nodeId) || (!url && nodeId) ? `https://www.benzinga.com/article/${nodeId}` : url;

                return {
                  link: articleLink,
                  pub_date: article.created as string,
                  title: article.title as string,
                } as Article;
              }) || [];

            setFoundedArticles(articles);
          });
      } catch (error) {
        console.error('ARTICLES ERROR:', error);
      }
      return articles;
    },
    [session],
  );

  useEffect(() => {
    if (query !== undefined) {
      setSearchQuery(query);
    }
  }, [query]);

  const resetSearchResults = useCallback(() => {
    setFoundedTickers([]);
    setFoundedArticles([]);
  }, []);

  const fetchInfo = React.useCallback(
    (searchQuery: string) => {
      if (!searchQuery) {
        resetSearchResults();
        return;
      }

      session.getManager(TrackingManager).trackSearchEvent('search', '', { query: searchQuery });

      if (fetchDebounce) {
        clearTimeout(fetchDebounce);
      }

      setIsFetching(true);
      if (onSearching) {
        onSearching(true);
      }

      const fetchDebounceTimeout = window.setTimeout(async () => {
        if (tickersSearch) {
          await fetchTickers(searchQuery).then(tickers => {
            const symbols = tickers.map(ticker => ticker.symbol);
            session
              .getManager(TrackingManager)
              .trackSearchEvent('search', 'debounce', { query: searchQuery, symbol: symbols.join(',') });
          });
        }

        if (articlesSearch) {
          await fetchArticles(searchQuery);
        }

        setIsFetching(false);
        if (onSearching) {
          onSearching(false);
        }

        setSelectedIndex(0);
      }, 250);

      setFetchDebounce(fetchDebounceTimeout);
    },
    [
      articlesSearch,
      fetchArticles,
      fetchDebounce,
      fetchTickers,
      onSearching,
      resetSearchResults,
      tickersSearch,
      session,
    ],
  );

  const onInput = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const query = event.target.value;
      setSearchQuery(query);
      fetchInfo(query);
    },
    [fetchInfo],
  );

  const onFocus = useCallback(() => {
    setShowDropdown(true);
    session.getManager(TrackingManager).trackSearchEvent('focus', '', { query: searchQuery });
  }, [session, searchQuery]);

  const onBlur = useCallback(() => {
    setSearchResultsHeight('auto');
    session.getManager(TrackingManager).trackSearchEvent('blur', '', { query: searchQuery });
  }, [session, searchQuery]);

  const handleClickCancel = useCallback(() => {
    onToggle && onToggle(false);
    session.getManager(TrackingManager).trackSearchEvent('cancel', '', { query: searchQuery });
    setShowDropdown(false);
    setSearchQuery('');
  }, [onToggle, session, searchQuery]);

  const focusOnSearchBar = () => {
    searchBarRef.current?.focus({ preventScroll: true });
  };

  const focusSearchElementByIndex = (index: number, elements: NodeListOf<Element>) => {
    const targetElement = elements[index] as HTMLElement;
    targetElement?.focus({ preventScroll: true });
    setSelectedIndex(index);
  };

  const selectTicker = useCallback(
    (ticker: Ticker) => {
      if (ticker.symbol) {
        session.getManager(TrackingManager).trackSearchEvent('results', '', {
          query: searchQuery,
          symbol: ticker.symbol,
        });
      }
      if (onSelect) {
        onSelect(ticker);
        resetSearchResults();
        setSearchQuery('');
        focusOnSearchBar();
      } else {
        if (!isFetching) {
          router.push(`/quote/${formatTickerToURLFriendly(ticker.symbol)}`);
          setShowDropdown(false);
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [onSelect, resetSearchResults, searchQuery, isFetching],
  );

  const onSearchKeyDown = React.useCallback(
    (event: React.KeyboardEvent<HTMLInputElement | HTMLAnchorElement>) => {
      const { key } = event;

      if (key === 'Escape') {
        setShowDropdown(false);
        setSelectedIndex(-1);
      } else if (key === 'Enter') {
        if (isFetching) {
          setPendingKeyDownTickerSearch(searchQuery);
          return;
        } else if (!isFetching && pendingKeyDownTickerSearch) {
          setPendingKeyDownTickerSearch(null);
        }

        if (foundedTickers?.length && selectedIndex >= 0 && foundedTickers[selectedIndex]) {
          selectTicker(foundedTickers[selectedIndex]);
        } else if (foundedTickers?.length && foundedTickers[0]?.symbol?.toLowerCase() === searchQuery?.toLowerCase()) {
          selectTicker(foundedTickers[0]);
        } else if (!isFetching) {
          const encodedSearchQuery = encodeURIComponent(searchQuery);
          const keywordsUrl = `/search?q=${encodedSearchQuery}`;
          router.push(keywordsUrl);
        }
      } else if (key === 'ArrowDown' || key === 'ArrowUp') {
        event.preventDefault();

        let localIndex = selectedIndex;
        const elements = document.querySelectorAll('.' + selectElementListClass);

        if (localIndex !== null) {
          event.stopPropagation();
          const elementsLastIndex = elements.length - 1;

          if (key === 'ArrowDown') {
            localIndex = localIndex + 1 > elementsLastIndex ? 0 : localIndex + 1;
          } else if (key === 'ArrowUp') {
            localIndex = localIndex - 1 < 0 ? elementsLastIndex : localIndex - 1;
          }

          if (elements[localIndex]) {
            focusSearchElementByIndex(localIndex, elements);
          }
        } else {
          if (elements[0]) {
            focusSearchElementByIndex(0, elements);
          }
        }
      } else if (key === 'Backspace') {
        focusOnSearchBar();
      } else {
        setShowDropdown(true);
        setSelectedIndex(-1);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [foundedTickers, isFetching, searchQuery, selectTicker, selectedIndex],
  );

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLInputElement>) => {
      onSearchKeyDown(e);
    },
    [onSearchKeyDown],
  );

  const ArticlesList: React.FC<{ list: Article[] }> = ({ list }) => {
    if (!list.length) return <NoResults />;

    const formatDate = (date: string): string => {
      return format(new Date(date), 'ccc MMM d yyyy HH:mm');
    };

    return (
      <>
        {list.map((article: Article, index: number) => (
          <ResultListItem
            className={selectElementListClass}
            date={formatDate(article.pub_date)}
            key={index}
            link={article.link}
            onKeyDown={onSearchKeyDown}
            title={article.title}
          />
        ))}
      </>
    );
  };

  const quoteLink = (symbol: string) => {
    const symbolReplacedSlashToDash = symbol?.replace('/', '-');
    return `/quote/${symbolReplacedSlashToDash}`;
  };

  const TickersList = ({ list, onResultClick }: { list: Ticker[]; onResultClick: (ticker: Ticker) => void }) => {
    if (!list.length) return <NoResults />;

    return (
      <>
        {list.map(
          (ticker: Ticker, index: number) =>
            (
              <ResultListItem
                change={ticker.change}
                changePercent={ticker.changePercent}
                className={selectElementListClass + (index === selectedIndex ? ' active' : '')}
                description={ticker.shortName}
                key={index}
                link={onSelect ? undefined : quoteLink(ticker.symbol)}
                logo={ticker.logo || ''}
                onClick={() => onResultClick(ticker)}
                onKeyDown={onSearchKeyDown}
                price={ticker.price}
                title={ticker.symbol}
              />
            ) as ReactElement,
        )}
      </>
    );
  };

  const handleOpenFullScreenSearch = () => {
    if (isFullScreenSearch) return;
    onToggle && onToggle(true);
  };

  return (
    <SearchWrapper className={`searchbar-wrapper searchbar-${variant}`} onClick={handleOpenFullScreenSearch} ref={ref}>
      <SearchContainer className="searchbar-container">
        <SearchBar
          autoFocus={autoFocus}
          handleClickCancel={handleClickCancel}
          handleRemove={handleRemove}
          inputRef={searchBarRef}
          isFullScreenSearch={isFullScreenSearch}
          onBlur={onBlur}
          onFocus={onFocus}
          onInput={onInput}
          onKeyDown={onKeyDown}
          placeholder={placeholder}
          searchQuery={searchQuery}
          selected={selected}
        />
        {Boolean(!isFetching && !!searchQuery && showDropdown) && (
          <SearchDropdown className="search-dropdown">
            <SearchResultWrapper className="search-result-wrapper" height={searchResultsHeight}>
              {tickersSearch && foundedTickers?.length ? (
                <SearchResultSection sectionName={searchType === 'all' ? 'Symbols' : ''}>
                  {TickersList({ list: foundedTickers, onResultClick: selectTicker })}
                </SearchResultSection>
              ) : null}
              {articlesSearch && foundedArticles?.length > 0 ? (
                <SearchResultSection sectionName={searchType === 'all' ? 'Articles' : ''}>
                  <ArticlesList list={foundedArticles} />
                </SearchResultSection>
              ) : null}
              {keywordsSearch && (
                <SearchResultSection sectionName={searchType === 'all' ? 'Keywords' : ''}>
                  <KeywordsLink onKeyDown={onSearchKeyDown} searchQuery={searchQuery} />
                </SearchResultSection>
              )}
            </SearchResultWrapper>
          </SearchDropdown>
        )}
      </SearchContainer>
    </SearchWrapper>
  );
};

const SearchContainer = styled.div`
  position: relative;
  width: 100%;
`;

const SearchDropdown = styled.div`
  border-radius: 4px;
  border: solid 1px ${({ theme }) => theme.colors.border};
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.12);
  position: absolute;
  overflow-y: auto;
  width: 100%;
  z-index: 50;
`;

const SearchResultWrapper = styled.div<{ height: string }>`
  background: ${props => props.theme.colors.background};
  padding: 12px 6px;
  border-radius: 4px;
  width: 100%;
  height: ${props => props.height};
  max-height: 70vh;
  overflow-y: auto;

  @media screen and (min-width: 800px) {
    height: auto;
  }
`;

interface ResultListItemInterface {
  logo?: string;
  change?: number;
  changePercent?: number;
  className?: string;
  date?: string;
  dateElement?: React.ReactElement;
  descriptionElement?: React.ReactElement;
  description?: string;
  hidePriceInfo?: boolean;
  link?: string;
  onClick?: React.MouseEventHandler<HTMLAnchorElement>;
  onKeyDown?: React.KeyboardEventHandler<HTMLAnchorElement>;
  price?: number;
  subtitle?: string;
  title: string;
}

const SearchResultSection = ({ children, sectionName }: { children: any; sectionName: string }) => {
  return (
    <div className="search-results-section">
      {sectionName?.length ? (
        <SearchResultSectionTitle className="search-results-title">
          <span>{sectionName}</span>
        </SearchResultSectionTitle>
      ) : null}
      <SearchResultsWrapper className="search-results-wrapper">{children}</SearchResultsWrapper>
    </div>
  );
};

const SearchResultSectionTitle = styled.div`
  background: ${({ theme }) => theme.colors.backgroundInactive};
  padding: 2px 10px;
  span {
    color: ${({ theme }) => theme.colorPalette.gray700};
    font-size: ${({ theme }) => theme.fontSize.base};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
  }
`;

const SearchResultsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  padding: 8px 0px;
`;

const NoResults = () => {
  return <NoResultsText>No Results</NoResultsText>;
};

const NoResultsText = styled.div`
  padding: 2px 10px;
`;

const ListItem: React.FC<ResultListItemInterface> = ({
  // change,
  // changePercent,
  className,
  date,
  dateElement,
  description,
  descriptionElement,
  link,
  // hidePriceInfo = false,
  logo,
  onClick,
  onKeyDown,
  subtitle,
  title,
}) => {
  // const isGreaterThan = changePercent ? changePercent > 0 : false;
  // const isLessThan = changePercent ? changePercent < 0 : false;
  return (
    <ListItemWrapper className={className} href={link} onClick={onClick} onKeyDown={onKeyDown} tabIndex={-1}>
      <div className="list-item-info">
        {logo !== undefined && <ListItemLogo className="list-item-logo" url={logo} />}
        <div>
          {subtitle && <span className="list-item-subtitle" dangerouslySetInnerHTML={{ __html: subtitle }} />}
          <div className="list-item-title" dangerouslySetInnerHTML={{ __html: title }} />
          {description && (
            <ListItemDescription className="list-item-description" dangerouslySetInnerHTML={{ __html: description }} />
          )}
          {descriptionElement && descriptionElement}
        </div>
      </div>
      {dateElement ? dateElement : date && <ListItemDate className="list-item-date">{date}</ListItemDate>}
    </ListItemWrapper>
  );
};

const ListItemWrapper = styled.a`
  cursor: pointer;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  padding: 2px 10px;
  .list-item-info {
    display: flex;
    align-items: center;
    padding: 2px 0;
  }
  .list-item-title {
    line-height: 1.1;
    font-size: ${({ theme }) => theme.fontSize.base};
    font-weight: ${({ theme }) => theme.fontWeight.semibold};
    &:hover {
      color: ${({ theme }) => theme.colorPalette.blue500};
    }
  }
  .list-item-subtitle {
    font-size: ${({ theme }) => theme.fontSize.sm};
  }
  .list-item-logo {
    margin-right: 10px;
  }
`;

const ListItemLogo = styled.div<{ url?: string }>`
  background: ${({ url }) => (url ? `url(${url})` : '#fff')};
  background-repeat: no-repeat;
  background-position: center center;
  height: ${SEARCH_ITEM_LOGO_SIZE}px;
  width: ${SEARCH_ITEM_LOGO_SIZE}px;
  flex-shrink: 0;
`;

const ListItemDescription = styled.small`
  .description {
    color: ${({ theme }) => theme.colors.foreground};
    font-size: ${({ theme }) => theme.fontSize.xs};
  }
`;

const ListItemDate = styled.small`
  color: ${({ theme }) => theme.colors.foreground};
  font-size: ${({ theme }) => theme.fontSize.sm};
`;

export const ResultListItem = styled(ListItem)`
  div {
    text-overflow: ellipsis;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    display: -webkit-box;
  }

  &:hover,
  &:focus,
  &.active {
    background-color: ${({ theme }) => theme.colors.backgroundActive};
    outline: none;
    div {
      color: ${({ theme }) => theme.colorPalette.blue500};
    }
  }

  .list-item-description {
    color: ${({ theme }) => theme.colorPalette.gray600};
  }
`;

const KeywordsLink = ({
  onKeyDown,
  searchQuery,
}: {
  onKeyDown: React.KeyboardEventHandler<HTMLAnchorElement>;
  searchQuery: string;
}) => {
  const encodedSearchQuery = encodeURIComponent(searchQuery);
  const keywordsLink = `/search?q=${encodedSearchQuery}`;

  return (
    <KeywordWrapper className="search-list-item" href={keywordsLink} onKeyDown={onKeyDown} tabIndex={-1}>
      Search by keyword...
    </KeywordWrapper>
  );
};

const KeywordWrapper = styled.a`
  color: ${props => props.theme.colorPalette.blue500};
  cursor: pointer;
  font-size: ${props => props.theme.fontSize.sm};
  outline: none;
  padding: 0 8px;
  &.focus {
    background: ${props => props.theme.colors.backgroundActive};
  }
  &.hover {
    color: ${props => props.theme.colorPalette.blue500};
  }
`;

const SearchWrapper = styled.div`
  &.searchbar-wrapper {
    box-shadow: 0px 0px 2px 0 rgb(0 0 0 / 15%);
    background: ${props => props.theme.colors.background};
    padding: 4px 12px;
    padding-right: 10px;
    flex-grow: 1;
    max-width: 290px;
    position: relative;
    max-width: none;

    @media screen and (max-width: 800px) {
      max-width: none;
    }

    a {
      text-decoration: none;
    }

    &.searchbar-box,
    &.searchbar-inline {
      padding: 0;
      border: solid 1px ${props => props.theme.colors.border};
      box-shadow: none;
      .search-results-wrapper {
        padding: 0;
      }
      .search-result-wrapper {
        padding: 0;
      }
      .search-icon {
        height: 36px;
      }
      input {
        ::placeholder {
          color: ${({ theme }) => theme.colorPalette.gray400};
        }
      }
    }

    &.searchbar-inline {
      box-shadow: none;
      .search-dropdown {
        position: relative;
        box-shadow: none;
        border: none;
      }
    }

    input {
      &:focus {
        box-shadow: none;
      }
      ::placeholder {
        color: ${({ theme }) => theme.colorPalette.gray600};
      }
    }
  }
`;
